import { useEffect, useState, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchMedications } from '../store/medicationListSlice'
import { MedicationListTable } from '../components/medications/MedicationListTable'
import { MedicationSearch } from '../components/medications/MedicationSearch'
import { AddMedicationModal } from '../components/medications/AddMedicationModal'
import { Plus } from 'lucide-react'

export function MedicationsPage() {
  const dispatch = useAppDispatch()
  const { medications, loading } = useAppSelector((s) => s.medicationList)

  const [modalOpen, setModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [controlledFilter, setControlledFilter] = useState('')

  useEffect(() => {
    dispatch(fetchMedications())
  }, [dispatch])

  const filteredMedications = useMemo(() => {
    return medications.filter((med) => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        if (!med.name.toLowerCase().includes(q)) {
          return false
        }
      }
      // Category filter
      if (categoryFilter && med.category !== categoryFilter) {
        return false
      }
      // Controlled filter
      if (controlledFilter) {
        const isControlled = med.controlled_substance
        if (controlledFilter === 'Yes' && !isControlled) return false
        if (controlledFilter === 'No' && isControlled) return false
      }
      return true
    })
  }, [medications, searchQuery, categoryFilter, controlledFilter])

  if (loading) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="medications-page">
        <p className="text-[13px] text-text-muted">Loading medications...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="medications-page">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[16px] font-semibold text-text-primary">Medications</h1>
        <button
          data-testid="add-medication-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          <Plus size={14} />
          <span className="max-sm:hidden">Add Medication</span>
        </button>
      </div>

      {/* Search and filters */}
      <div className="mb-4">
        <MedicationSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          controlledFilter={controlledFilter}
          onControlledFilterChange={setControlledFilter}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <MedicationListTable medications={filteredMedications} />
      </div>

      {/* Add Medication Modal */}
      <AddMedicationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
